/**
 * Documents Panel — base64 upload to MongoDB.
 * Phase 1: PDF / images.
 */
import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Upload, FileText, Trash2, Download, Loader2, FileImage, FileSignature } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICONS = {
  consenso: FileSignature,
  referto: FileText,
  radiografia: FileImage,
  preventivo: FileText,
  document: FileText,
};

const TYPE_LABELS = {
  consenso: "Consenso",
  referto: "Referto",
  radiografia: "Radiografia",
  preventivo: "Preventivo",
  document: "Documento",
};

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const DocumentsPanel = ({ clientId, initial, onChange, t }) => {
  const [items, setItems] = useState(initial || []);
  const [type, setType] = useState("document");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  React.useEffect(() => { setItems(initial || []); }, [initial]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("File troppo grande (max 5MB)", "File too large (max 5MB)"));
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      // Read as base64 (without data: prefix)
      const reader = new FileReader();
      const b64 = await new Promise((res, rej) => {
        reader.onerror = rej;
        reader.onload = () => res(String(reader.result || "").split(",", 2)[1] || "");
        reader.readAsDataURL(file);
      });
      const r = await axios.post(`${API}/clients/${clientId}/documents`, {
        type,
        filename: file.name,
        content_base64: b64,
        mime: file.type || "application/octet-stream",
      }, { withCredentials: true });
      setItems([r.data, ...items]);
      toast.success(t("Documento caricato", "Document uploaded"));
      if (onChange) onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("Errore upload", "Upload error"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const r = await axios.get(`${API}/clients/${clientId}/documents/${doc.doc_id}`, { withCredentials: true });
      const a = document.createElement("a");
      a.href = `data:${r.data.mime || "application/octet-stream"};base64,${r.data.content_base64}`;
      a.download = r.data.filename || "documento";
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error(t("Errore download", "Download error"));
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(t("Eliminare definitivamente?", "Delete permanently?"))) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/documents/${doc.doc_id}`, { withCredentials: true });
      setItems(items.filter(d => d.doc_id !== doc.doc_id));
      toast.success(t("Eliminato", "Deleted"));
      if (onChange) onChange();
    } catch {
      toast.error(t("Errore", "Error"));
    }
  };

  return (
    <div className="space-y-4" data-testid="client-documents-panel">
      {/* Uploader */}
      <div className="rounded-2xl border border-dashed border-violet-200 dark:border-violet-900/30 bg-violet-50/30 dark:bg-violet-950/10 p-4 flex items-center gap-3 flex-wrap">
        <Upload className="w-5 h-5 text-violet-500 flex-shrink-0" />
        <div className="flex-1 min-w-[160px]">
          <p className="text-sm font-semibold">{t("Carica documento", "Upload document")}</p>
          <p className="text-[11px] text-muted-foreground">{t("PDF o immagini · max 5MB", "PDF or images · max 5MB")}</p>
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[150px] h-9 text-xs rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.keys(TYPE_LABELS).map(k => (
              <SelectItem key={k} value={k}>{TYPE_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input ref={inputRef} type="file" accept="application/pdf,image/*" onChange={onFile}
               className="hidden" data-testid="client-doc-file-input" />
        <Button
          size="sm" className="rounded-full bg-violet-600 hover:bg-violet-700"
          onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid="client-doc-upload-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" />{t("Carica", "Upload")}</>}
        </Button>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-white/10 p-10 text-center">
          <FileText className="w-8 h-8 mx-auto text-slate-400" />
          <p className="font-semibold text-sm mt-3">{t("Nessun documento", "No documents")}</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map(d => {
            const Icon = TYPE_ICONS[d.type] || FileText;
            return (
              <li key={d.doc_id} className="rounded-2xl border border-slate-200/60 dark:border-white/10 p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-violet-600 dark:text-violet-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{d.filename}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {TYPE_LABELS[d.type] || "—"} · {fmtBytes(d.size_bytes)} · {fmtDate(d.created_at)}
                    {d.uploaded_by_name && <> · {d.uploaded_by_name}</>}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" onClick={() => download(d)} data-testid={`client-doc-download-${d.doc_id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                        onClick={() => remove(d)} data-testid={`client-doc-delete-${d.doc_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default DocumentsPanel;
